import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { nip19 } from 'nostr-tools';
import { ArrowLeft, Link2, Pencil, Trash2, UserPlus } from 'lucide-react';
import { SiteFooter, SiteHeader } from '@/components/SiteHeader';
import { MemberListSkeleton, MemberRow } from '@/components/MemberRow';
import { CopyNpub } from '@/components/CopyNpub';
import { useFollowPack } from '@/hooks/useFollowPacks';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useFollowAll } from '@/hooks/useFollowActions';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { useAuthor } from '@/hooks/useAuthor';
import { displayNameOf, ProfileAvatar } from '@/components/Avatar';
import { timeAgo } from '@/lib/format';
import { sanitizeImageUrl } from '@/lib/sanitize';
import { FOLLOW_PACK_KIND } from '@/lib/followPacks';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useQueryClient } from '@tanstack/react-query';

export default function PackDetailPage({ naddrOverride }: { naddrOverride?: boolean } = {}) {
  const params = useParams<{ pubkey: string; d: string }>();
  const idParam = useParams<{ nip19: string }>();

  let pubkey = params.pubkey;
  let d = params.d ? decodeURIComponent(params.d) : '';

  if (naddrOverride && idParam.nip19) {
    try {
      const decoded = nip19.decode(idParam.nip19);
      if (decoded.type === 'naddr') {
        pubkey = decoded.data.pubkey;
        d = decoded.data.identifier;
      }
    } catch { /* falls through to not-found */ }
  }
  const decodedD = d;
  const validPubkey = pubkey && /^[0-9a-f]{64}$/i.test(pubkey) ? pubkey.toLowerCase() : undefined;

  const { data, isLoading, isError } = useFollowPack(validPubkey, decodedD || undefined);
  const pack = data?.pack ?? null;

  const { data: authorData } = useAuthor(pack?.pubkey);
  const authorName = useMemo(
    () => (pack ? displayNameOf(authorData?.metadata, pack.pubkey) : ''),
    [authorData?.metadata, pack],
  );

  useSeoMeta({
    title: pack ? `${pack.name} — Follow Party` : 'Follow Party pack',
    description: pack?.description || (pack ? `A Nostr follow pack with ${pack.members.length} people.` : 'A Nostr follow pack.'),
  });

  const { user } = useCurrentUser();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const followAll = useFollowAll(pack?.members.map((m) => m.pubkey) ?? []);
  const { mutateAsync: publish, isPending: isPublishing } = useNostrPublish();
  const [copied, setCopied] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const isOwner = Boolean(user && pack && user.pubkey === pack.pubkey);
  const cover = pack?.coverImageUrl ? sanitizeImageUrl(pack.coverImageUrl) : null;

  const shareUrl = pack ? `${window.location.origin}/pack/${pack.pubkey}/${encodeURIComponent(pack.id)}` : '';
  const naddr = useMemo(() => {
    if (!pack) return '';
    try {
      return nip19.naddrEncode({ kind: FOLLOW_PACK_KIND, pubkey: pack.pubkey, identifier: pack.id });
    } catch {
      return '';
    }
  }, [pack]);

  const copyShare = async () => {
    if (!pack) return;
    const text = naddr ? `nostr:${naddr}` : shareUrl;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast({ title: 'Link copied', description: 'Share this pack anywhere.' });
  };

  const doFollowAll = async () => {
    if (!user) {
      toast({ title: 'Log in to follow everyone', description: 'You need to be logged in first.' });
      return;
    }
    const added = await followAll.mutateAsync();
    setConfirmOpen(false);
    toast({
      title: added === 0 ? 'Already following everyone' : `Following ${added} new ${added === 1 ? 'person' : 'people'}`,
      description: added === 0 ? 'This pack holds no new faces for you.' : 'Your follow list was updated.',
    });
  };

  const doDelete = async () => {
    if (!pack || !user) return;
    await publish({
      kind: 5,
      content: 'Follow pack deleted by owner',
      tags: [
        ['e', pack.eventId],
        ['a', `${FOLLOW_PACK_KIND}:${pack.pubkey}:${pack.id}`],
        ['k', String(FOLLOW_PACK_KIND)],
      ],
    });
    await queryClient.invalidateQueries({ queryKey: ['follow-packs'] });
    await queryClient.invalidateQueries({ queryKey: ['follow-pack'] });
    toast({ title: 'Pack deleted' });
    navigate('/');
  };

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="container flex-1 px-4 py-5 sm:px-8 sm:py-8">
        <Button asChild variant="ghost" className="mb-3 rounded-full sm:mb-4">
          <Link to="/">
            <ArrowLeft className="size-4" />
            All packs
          </Link>
        </Button>

        {isLoading ? (
          <PackDetailSkeleton />
        ) : isError || !pack ? (
          <Card className="border-dashed">
            <CardContent className="px-8 py-12 text-center">
              <p className="mx-auto max-w-sm text-muted-foreground">
                This pack couldn't be found. It may have been deleted, or your relays may not have it yet.
              </p>
              <Button asChild className="mt-4 rounded-full">
                <Link to="/">Back to packs</Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid min-w-0 gap-5 sm:gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
            <div className="min-w-0">
              <Card className="overflow-hidden rounded-2xl">
                <div className="relative h-40 w-full bg-muted sm:h-64">
                  {cover ? (
                    <img
                      src={cover}
                      alt=""
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="grid h-full place-items-center overflow-hidden bg-[linear-gradient(120deg,hsl(84_75%_62%),hsl(200_70%_55%),hsl(280_65%_55%))] p-4 text-center font-display text-2xl text-white sm:p-6 sm:text-5xl">
                      <span className="line-clamp-3 break-words">{pack.name}</span>
                    </div>
                  )}
                </div>
                <CardContent className="p-4 sm:p-6">
                  <h1 className="break-words text-2xl font-bold tracking-tight sm:text-4xl">{pack.name}</h1>
                  <div className="mt-3 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1.5 text-sm text-muted-foreground">
                    <ProfileAvatar pubkey={pack.pubkey} size="xs" />
                    <span className="min-w-0">
                      <span className="block max-w-full truncate font-medium text-foreground">by {authorName}</span>
                      <CopyNpub pubkey={pack.pubkey} />
                    </span>
                    <span aria-hidden>·</span>
                    <span className="shrink-0">{pack.members.length} {pack.members.length === 1 ? 'person' : 'people'}</span>
                    <span aria-hidden>·</span>
                    <span className="shrink-0">updated {timeAgo(pack.createdAt)}</span>
                  </div>
                  {pack.description ? (
                    <p className="mt-4 break-words whitespace-pre-wrap text-base leading-relaxed sm:text-[17px]">{pack.description}</p>
                  ) : null}
                </CardContent>
              </Card>

              <h2 className="mb-2 mt-6 text-lg font-semibold sm:mt-8 sm:text-xl">Members</h2>
              <Card className="min-w-0">
                <CardContent className="divide-y px-3 sm:px-5">
                  {pack.members.map((m) => (
                    <MemberRow key={m.pubkey} pubkey={m.pubkey} />
                  ))}
                </CardContent>
              </Card>
            </div>

            <aside className="order-first min-w-0 lg:order-none lg:sticky lg:top-24 lg:self-start">
              <Card className="rounded-2xl">
                <CardContent className="space-y-3 p-5">
                  <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                    <AlertDialogTrigger asChild>
                      <Button size="lg" className="w-full rounded-full" disabled={!pack.members.length}>
                        <UserPlus className="size-4" />
                        Follow all ({pack.members.length})
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Follow all {pack.members.length} people?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This adds everyone in "{pack.name}" to your follow list. People you already
                          follow are kept — nothing gets removed. You can unfollow anyone later.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={doFollowAll} disabled={followAll.isPending || isPublishing}>
                          {followAll.isPending ? 'Following…' : 'Yes, follow all'}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>

                  <Button variant="outline" className="w-full rounded-full" onClick={copyShare}>
                    <Link2 className="size-4" />
                    {copied ? 'Copied!' : 'Copy share link'}
                  </Button>
                  {naddr ? (
                    <p className="break-all rounded-xl bg-muted p-3 font-mono text-[11px] text-muted-foreground">
                      nostr:{naddr}
                    </p>
                  ) : null}

                  {isOwner ? (
                    <div className="flex gap-2 pt-1">
                      <Button asChild variant="secondary" className="flex-1 rounded-full">
                        <Link to={`/edit/${pack.pubkey}/${encodeURIComponent(pack.id)}`}>
                          <Pencil className="size-4" />
                          Edit
                        </Link>
                      </Button>
                      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                        <AlertDialogTrigger asChild>
                          <Button variant="destructive" className="flex-1 rounded-full">
                            <Trash2 className="size-4" />
                            Delete
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this pack?</AlertDialogTitle>
                            <AlertDialogDescription>
                              A deletion request is published to relays. Some relays may keep a copy
                              for a while.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={doDelete}>Delete pack</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  ) : null}

                  <p className="text-xs leading-relaxed text-muted-foreground">
                    Follow Party packs are portable: the same pack also works on other kind 39089 apps.
                  </p>
                </CardContent>
              </Card>
            </aside>
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}

function PackDetailSkeleton() {
  return (
    <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
      <div className="h-96 animate-pulse rounded-2xl bg-muted" />
      <MemberListSkeleton rows={4} />
    </div>
  );
}
